import * as React from 'react';

import { cn } from '@/lib/utils';

import { buttonGroupVariants, buttonGroupItemVariants } from './ButtonGroup.styles';

import type { ButtonGroupProps, ButtonGroupItem } from './ButtonGroup.types';

/**
 * ButtonGroup component
 *
 * A group of connected icon buttons, typically used for toolbar-style interfaces.
 * Based on Figma Design System Nabat 2.0 - Button Group
 *
 * @example
 * // Vertical button group (default)
 * <ButtonGroup
 *   items={[
 *     { id: '1', icon: <Settings />, 'aria-label': 'Settings', onClick: handleSettings },
 *     { id: '2', icon: <Info />, 'aria-label': 'Info', onClick: handleInfo },
 *     { id: '3', icon: <Phone />, 'aria-label': 'Phone', onClick: handlePhone },
 *   ]}
 * />
 *
 * @example
 * // Horizontal button group with selection
 * <ButtonGroup
 *   orientation="horizontal"
 *   items={[
 *     { id: '1', icon: <AlignLeft />, 'aria-label': 'Align left', selected: true },
 *     { id: '2', icon: <AlignCenter />, 'aria-label': 'Align center' },
 *     { id: '3', icon: <AlignRight />, 'aria-label': 'Align right' },
 *   ]}
 * />
 */
export const ButtonGroup = React.forwardRef<HTMLDivElement, ButtonGroupProps>(
  (
    {
      className,
      items,
      appearance = 'solid',
      kind = 'neutral',
      scale = 'm',
      orientation = 'vertical',
      ...props
    },
    ref
  ) => {
    const getPosition = (index: number, total: number): 'first' | 'middle' | 'last' | 'only' => {
      if (total === 1) return 'only';
      if (index === 0) return 'first';
      if (index === total - 1) return 'last';
      return 'middle';
    };

    return (
      <div
        ref={ref}
        role="group"
        className={cn(buttonGroupVariants({ orientation, className }))}
        {...props}
      >
        {items.map((item: ButtonGroupItem, index: number) => {
          const position = getPosition(index, items.length);

          return (
            <button
              key={item.id}
              type="button"
              onClick={item.onClick}
              disabled={item.disabled}
              aria-label={item['aria-label']}
              aria-pressed={item.selected ?? false}
              className={cn(
                buttonGroupItemVariants({
                  appearance,
                  kind,
                  scale,
                  position,
                  orientation,
                  selected: item.selected || false,
                })
              )}
            >
              <span className="shrink-0">{item.icon}</span>
            </button>
          );
        })}
      </div>
    );
  }
);

ButtonGroup.displayName = 'ButtonGroup';
